import {
  CustomNode,
  NodeType,
  NodeCategory,
  NodeFile,
  ArtifactNodeData,
  NodeResult,
  NodeInputType,
  HandleType,
} from '@/types/node-types';
import { CustomEdge, ConnectionType } from '@/types/edge-types';
import {
  ARTIFACT_HORIZONTAL_OFFSET,
  ARTIFACT_VERTICAL_STRIDE,
} from '@/constants/artifact-styling';
import { ARTIFACT_HANDLE_ID } from '@/pages/workflow/component/custom-node/artifact-node/artifact-node';

export function isArtifactNode(node: CustomNode): boolean {
  return node.type === NodeType.ArtifactNode;
}

export function isArtifactEdge(edge: CustomEdge): boolean {
  return Boolean(
    (edge.data as { isArtifact?: boolean } | undefined)?.isArtifact,
  );
}

function extractFileMetadata(result?: NodeResult): {
  fileDetail?: string;
} {
  if (result?.metadata) {
    const { character_count, line_count, word_count } = result.metadata;
    const details: string[] = [];
    if (word_count) details.push(`${word_count} words`);
    if (line_count) details.push(`${line_count} lines`);
    if (character_count && !word_count)
      details.push(`${character_count} chars`);

    if (details.length > 0) {
      return { fileDetail: details.join(' · ') };
    }
  }
  return {};
}

const ARTIFACT_CARD_WIDTH = 280;
const ARTIFACT_CARD_HEIGHT = 120;
const COLLISION_PADDING = 20;

function calculatePositions(
  parentNode: CustomNode,
  fileCount: number,
  occupiedRects?: { x: number; y: number; w: number; h: number }[],
): { x: number; y: number }[] {
  const parentWidth = Number(parentNode.measured?.width ?? 420);
  const parentHeight = Number(parentNode.measured?.height ?? 200);
  const parentX = Number(parentNode.position.x);
  const parentY = Number(parentNode.position.y);

  const totalHeight = (fileCount - 1) * ARTIFACT_VERTICAL_STRIDE;
  const x = parentX + parentWidth + ARTIFACT_HORIZONTAL_OFFSET;
  let startY = parentY + parentHeight / 2 - totalHeight / 2;

  // When a node sits between the producer and the card's column (e.g. an eval
  // node downstream of the producer), drop the card into a vertical lane below
  // it rather than pushing it further right — that keeps the connecting edge on
  // the producer's side instead of routing it across the intervening node.
  if (occupiedRects && occupiedRects.length > 0) {
    for (let attempt = 0; attempt < 5; attempt++) {
      let blocked = false;
      const minY = startY;
      const maxY = startY + totalHeight + ARTIFACT_CARD_HEIGHT;
      for (const rect of occupiedRects) {
        const hOverlap =
          x < rect.x + rect.w + COLLISION_PADDING &&
          x + ARTIFACT_CARD_WIDTH + COLLISION_PADDING > rect.x;
        const vOverlap =
          minY < rect.y + rect.h + COLLISION_PADDING &&
          maxY + COLLISION_PADDING > rect.y;
        if (hOverlap && vOverlap) {
          startY = rect.y + rect.h + COLLISION_PADDING;
          blocked = true;
          break;
        }
      }
      if (!blocked) break;
    }
  }

  return Array.from({ length: fileCount }, (_, i) => ({
    x,
    y: startY + i * ARTIFACT_VERTICAL_STRIDE,
  }));
}

export const ARTIFACT_ELIGIBLE_CATEGORIES = new Set<string>([
  NodeCategory.AGENT,
  NodeCategory.OUTPUT,
  NodeCategory.GOVERNANCE,
  NodeCategory.TOOL,
  NodeCategory.DATA,
]);

function isEvalNode(node: CustomNode): boolean {
  return node.data?.struct?.node_sub_type === 'evals';
}

const UNRESOLVED_TARGET = '__unresolved_target__';

function resolveEdgeSourceId(
  edge: CustomEdge,
  idToNodeId: Map<string, string>,
): string | undefined {
  const candidates = [
    edge.source,
    (edge as { source_node_id?: string }).source_node_id,
    (edge as { sourceNodeId?: string }).sourceNodeId,
  ];
  for (const id of candidates) {
    if (id && idToNodeId.has(id)) return idToNodeId.get(id);
  }
  return undefined;
}

function resolveEdgeTargetId(
  edge: CustomEdge,
  idToNodeId: Map<string, string>,
): string {
  const candidates = [
    edge.target,
    (edge as { target_node_id?: string }).target_node_id,
    (edge as { targetNodeId?: string }).targetNodeId,
  ];
  for (const id of candidates) {
    if (id && idToNodeId.has(id)) return idToNodeId.get(id)!;
  }
  return UNRESOLVED_TARGET;
}

// Eval nodes emit a verdict, never an artifact, so an eval at the end of a
// chain would be the sole terminal node and the real artifact would never
// surface. Edges into one are treated as non-consuming.
export function getTerminalNodeIds(
  nodes: CustomNode[],
  edges: CustomEdge[],
): Set<string> {
  const nonArtifactEdges = edges.filter((e) => !isArtifactEdge(e));

  const idToNodeId = new Map<string, string>();
  const evalNodeIds = new Set<string>();
  for (const node of nodes) {
    idToNodeId.set(node.id, node.id);
    const rfId = node.data?.struct?.react_flow_generated_id;
    if (rfId) idToNodeId.set(rfId, node.id);
    if (isEvalNode(node)) evalNodeIds.add(node.id);
  }

  const outgoing = new Map<string, Set<string>>();
  for (const e of nonArtifactEdges) {
    const sourceId = resolveEdgeSourceId(e, idToNodeId);
    if (!sourceId) continue;
    const targetId = resolveEdgeTargetId(e, idToNodeId);
    if (!outgoing.has(sourceId)) outgoing.set(sourceId, new Set());
    outgoing.get(sourceId)!.add(targetId);
  }

  // An eval node is transparent when every downstream node is itself a
  // transparent eval node (vacuously true when it has no outgoing edges).
  const transparentMemo = new Map<string, boolean>();
  const isTransparentEval = (nodeId: string, stack: Set<string>): boolean => {
    if (!evalNodeIds.has(nodeId)) return false;
    const memoized = transparentMemo.get(nodeId);
    if (memoized !== undefined) return memoized;
    if (stack.has(nodeId)) return false;
    stack.add(nodeId);
    const targets = outgoing.get(nodeId);
    let transparent = true;
    if (targets) {
      for (const target of targets) {
        if (!isTransparentEval(target, stack)) {
          transparent = false;
          break;
        }
      }
    }
    stack.delete(nodeId);
    transparentMemo.set(nodeId, transparent);
    return transparent;
  };

  const realSources = new Set<string>();
  for (const [sourceId, targets] of outgoing) {
    for (const target of targets) {
      if (!isTransparentEval(target, new Set())) {
        realSources.add(sourceId);
        break;
      }
    }
  }

  const terminalIds = new Set<string>();
  for (const node of nodes) {
    if (isArtifactNode(node)) continue;
    if (!realSources.has(node.id)) {
      terminalIds.add(node.id);
    }
  }
  return terminalIds;
}

function getOccupiedRects(
  parentNode: CustomNode,
  allNodes?: CustomNode[],
): { x: number; y: number; w: number; h: number }[] | undefined {
  const parentRight =
    Number(parentNode.position.x) + Number(parentNode.measured?.width ?? 420);
  return allNodes
    ?.filter((n) => n.id !== parentNode.id)
    .filter((n) => {
      const nx = Number(n.position.x);
      const nw = Number(
        n.measured?.width ?? (isArtifactNode(n) ? ARTIFACT_CARD_WIDTH : 420),
      );
      return nx + nw > parentRight;
    })
    .map((n) => ({
      x: Number(n.position.x),
      y: Number(n.position.y),
      w: Number(
        n.measured?.width ?? (isArtifactNode(n) ? ARTIFACT_CARD_WIDTH : 420),
      ),
      h: Number(
        n.measured?.height ?? (isArtifactNode(n) ? ARTIFACT_CARD_HEIGHT : 200),
      ),
    }));
}

function getArtifactSourceHandle(parentNode: CustomNode): string {
  const outputPorts = parentNode.data?.struct?.output_ports;
  const filePort = outputPorts?.find((p) =>
    p.port_type?.accepted_types?.includes(HandleType.FILE),
  );
  return filePort?.id ?? outputPorts?.[0]?.id ?? '';
}

function buildArtifactNodeAndEdge(
  parentNode: CustomNode,
  nodeId: string,
  position: { x: number; y: number },
  artifactData: ArtifactNodeData,
  sourceHandle: string,
  inferredType: string,
): { node: CustomNode; edge: CustomEdge } {
  const node = {
    id: nodeId,
    type: NodeType.ArtifactNode,
    position,
    data: artifactData as unknown as CustomNode['data'],
    selectable: true,
    draggable: true,
    deletable: false,
  } as CustomNode;

  const edge = {
    id: `artifact-edge-${nodeId}`,
    source: parentNode.id,
    sourceHandle,
    target: nodeId,
    targetHandle: ARTIFACT_HANDLE_ID,
    type: inferredType,
    edge_type: ConnectionType.PORT_TO_PORT,
    data: { isArtifact: true },
    deletable: false,
  } as CustomEdge;

  return { node, edge };
}

function spawnArtifactNodes(
  parentNode: CustomNode,
  files: NodeFile[],
  result?: NodeResult,
  existingEdges?: CustomEdge[],
  allNodes?: CustomNode[],
): { newNodes: CustomNode[]; newEdges: CustomEdge[] } {
  if (!files.length) return { newNodes: [], newEdges: [] };

  const nodeCategory = parentNode.data?.struct?.node_type;
  if (nodeCategory && !ARTIFACT_ELIGIBLE_CATEGORIES.has(nodeCategory)) {
    return { newNodes: [], newEdges: [] };
  }

  const { fileDetail } = extractFileMetadata(result);
  const occupiedRects = getOccupiedRects(parentNode, allNodes);
  const positions = calculatePositions(parentNode, files.length, occupiedRects);

  const newNodes: CustomNode[] = [];
  const newEdges: CustomEdge[] = [];

  const sourceHandle = getArtifactSourceHandle(parentNode);
  const inferredType =
    existingEdges?.find((e) => e.type === 'orthogonal' || e.type === 'default')
      ?.type ?? 'default';

  files.forEach((file, i) => {
    const artifactData: ArtifactNodeData = {
      file,
      parentNodeId: parentNode.id,
      fileDetail,
    };
    const { node, edge } = buildArtifactNodeAndEdge(
      parentNode,
      `artifact-${parentNode.id}-${i}`,
      positions[i],
      artifactData,
      sourceHandle,
      inferredType,
    );
    newNodes.push(node);
    newEdges.push(edge);
  });

  return { newNodes, newEdges };
}

interface DisplayResult {
  files?: NodeFile[];
  display_modal?: NodeInputType | string;
  value?: unknown;
}

// Audio nodes (e.g. text-to-speech) emit the generated file's URL in
// ``value`` with a ``display_modal`` marker rather than populating
// ``files``. Treat it as a real file artifact since the URL already points
// at generated audio, not inline content to preview.
function extractAudioFile(
  result: DisplayResult,
  parentNodeId: string,
  nodeName: string,
): NodeFile | null {
  if (
    result.display_modal !== NodeInputType.AUDIO ||
    typeof result.value !== 'string' ||
    !result.value
  ) {
    return null;
  }

  return {
    name: `${nodeName} Output.mp3`,
    mime_type: 'audio/mpeg',
    presigned_url: result.value,
    s3_bucket: '',
    s3_key: `${parentNodeId}-audio-output`,
    s3_region: '',
    generated: true,
  };
}

// Display nodes (Markdown / Text) emit their body in ``value`` with a
// ``display_modal`` marker and no backing file. Mirror the Files-tab
// virtual-file logic so the canvas shows a downloadable artifact card too.
function extractContentArtifact(
  result: DisplayResult,
  nodeSubType?: string,
): { name: string; content: string; contentType: 'markdown' | 'text' } | null {
  if (result.value === undefined || nodeSubType === 'file_input') return null;

  if (result.display_modal === NodeInputType.MARKDOWN) {
    return {
      name: 'Output.md',
      content: String(result.value),
      contentType: 'markdown',
    };
  }
  if (result.display_modal === NodeInputType.TEXT_OUTPUT) {
    const content =
      typeof result.value === 'string'
        ? result.value
        : JSON.stringify(result.value, null, 2);
    return { name: 'Output.txt', content, contentType: 'text' };
  }
  return null;
}

function spawnContentArtifactNode(
  parentNode: CustomNode,
  spec: { name: string; content: string; contentType: 'markdown' | 'text' },
  existingEdges?: CustomEdge[],
  allNodes?: CustomNode[],
): { newNodes: CustomNode[]; newEdges: CustomEdge[] } {
  const nodeCategory = parentNode.data?.struct?.node_type;
  if (nodeCategory && !ARTIFACT_ELIGIBLE_CATEGORIES.has(nodeCategory)) {
    return { newNodes: [], newEdges: [] };
  }

  const struct = parentNode.data?.struct;
  const nodeName =
    struct?.display_name || struct?.user_config_name || 'Untitled Node';
  const occupiedRects = getOccupiedRects(parentNode, allNodes);
  const [position] = calculatePositions(parentNode, 1, occupiedRects);
  const sourceHandle = getArtifactSourceHandle(parentNode);
  const inferredType =
    existingEdges?.find((e) => e.type === 'orthogonal' || e.type === 'default')
      ?.type ?? 'default';

  const artifactData: ArtifactNodeData = {
    file: {
      name: `${nodeName} ${spec.name}`,
      mime_type:
        spec.contentType === 'markdown' ? 'text/markdown' : 'text/plain',
      s3_key: `${parentNode.id}-content-output`,
      s3_bucket: '',
      s3_region: '',
      presigned_url: '',
      generated: true,
    },
    parentNodeId: parentNode.id,
    content: spec.content,
    contentType: spec.contentType,
  };

  const { node, edge } = buildArtifactNodeAndEdge(
    parentNode,
    `artifact-${parentNode.id}-content`,
    position,
    artifactData,
    sourceHandle,
    inferredType,
  );

  return { newNodes: [node], newEdges: [edge] };
}

// Unified entry point: spawn file artifacts when the result carries files,
// otherwise spawn a single content artifact for markdown/text display output.
export function spawnArtifactNodesForResult(
  parentNode: CustomNode,
  result: NodeResult | undefined,
  existingEdges?: CustomEdge[],
  allNodes?: CustomNode[],
): { newNodes: CustomNode[]; newEdges: CustomEdge[] } {
  const displayResult = (result ?? {}) as DisplayResult;

  if (displayResult.files && displayResult.files.length > 0) {
    return spawnArtifactNodes(
      parentNode,
      displayResult.files,
      result,
      existingEdges,
      allNodes,
    );
  }

  const struct = parentNode.data?.struct;
  const nodeName =
    struct?.display_name || struct?.user_config_name || 'Untitled Node';
  const audioFile = extractAudioFile(displayResult, parentNode.id, nodeName);
  if (audioFile) {
    return spawnArtifactNodes(
      parentNode,
      [audioFile],
      result,
      existingEdges,
      allNodes,
    );
  }

  const contentSpec = extractContentArtifact(
    displayResult,
    parentNode.data?.struct?.node_sub_type,
  );
  if (contentSpec) {
    return spawnContentArtifactNode(
      parentNode,
      contentSpec,
      existingEdges,
      allNodes,
    );
  }

  return { newNodes: [], newEdges: [] };
}

// True when this result would surface as an on-canvas artifact card — a real
// file output or a markdown/text display output. Used to suppress the node's
// floating control bar (the artifact card replaces it), keeping markdown
// consistent with file outputs.
export function resultProducesArtifact(
  result: NodeResult | undefined,
  nodeSubType?: string,
): boolean {
  const displayResult = (result ?? {}) as DisplayResult;
  if (displayResult.files && displayResult.files.length > 0) return true;
  if (
    displayResult.display_modal === NodeInputType.AUDIO &&
    typeof displayResult.value === 'string' &&
    displayResult.value
  ) {
    return true;
  }
  return extractContentArtifact(displayResult, nodeSubType) !== null;
}

export function removeAllArtifactNodes(
  nodes: CustomNode[],
  edges: CustomEdge[],
): { filteredNodes: CustomNode[]; filteredEdges: CustomEdge[] } {
  const artifactNodeIds = new Set(
    nodes.filter(isArtifactNode).map((n) => n.id),
  );

  return {
    filteredNodes: nodes.filter((n) => !artifactNodeIds.has(n.id)),
    filteredEdges: edges.filter(
      (e) => !artifactNodeIds.has(e.target) && !artifactNodeIds.has(e.source),
    ),
  };
}

export function repositionArtifactNodes(
  parentNode: CustomNode,
  nodes: CustomNode[],
): CustomNode[] {
  const artifactChildren = nodes.filter(
    (n) =>
      isArtifactNode(n) &&
      (n.data as unknown as ArtifactNodeData)?.parentNodeId === parentNode.id,
  );

  if (artifactChildren.length === 0) return nodes;

  const positions = calculatePositions(parentNode, artifactChildren.length);

  const positionMap = new Map(
    artifactChildren.map((n, i) => [n.id, positions[i]]),
  );

  return nodes.map((node) => {
    const newPos = positionMap.get(node.id);
    if (newPos) {
      return { ...node, position: newPos };
    }
    return node;
  });
}
